<template>
  <div
    ref="folderNameContainer"
    class="folder-name-container"
  >
    <!-- The style binding on this div is to provide a max indentation level for a cleaner UI -->
    <div
      data-testid="folder-card"
      :draggable="canEdit"
      :class="['folder-card', dragHover, { 'picking': showPicker, 'editing-feature': editing }]"
      :style="`padding-left:${editing ? 0 : depth <= 10 ? depth * 15 : 150}px;`"
      @click="folderClick"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
      @dragenter.stop.prevent="handleDragClass"
      @dragleave.stop.prevent="handleDragClass"
      @dragend="dragEnd"
      @dragstart.stop="dragHandler"
      @drop.stop="folderDrop"
      @dragover.stop.prevent="handleDragClass"
    >
      <div class="action-items">
        <!-- Style binding here is for rotating caret icon -->
        <CaretIcon
          :style="`
            height: 30;
            width: 30;
            transform: rotate(${showBody ? 0 : -90}deg)
          `"
          @click.stop.prevent="$emit('updateFolder', 'showBody', !showBody)"
        />
        <!-- Show Drag Icon on hover -->
        <DragIcon
          v-if="hovered && canEdit"
          style="margin-right: 10px"
        />
        <!-- Or just show the Folder icon statically -->
        <div
          v-else
          class="folder-icon-container"
        >
          <component
            :is="selectedIcon"
            :style="{
              '--feature-color': color,
              'fill': markerColor
            }"
            :class="iconClass"
          />
        </div>
      </div>
      <span
        v-show="!editing"
        ref="viewFolderName"
        class="folder-card-name"
        data-testid="folder-name"
      >{{ name }}</span>
      <!-- Edit mode input for changing folder name -->
      <input
        v-show="editing"
        ref="folderName"
        v-model="name"
        class="outlined-input"
        data-testid="folder-name-input"
        type="text"
        :disabled="!canEdit"
        @click.stop
        @blur="handleSave"
        @keyup.enter="(e) => e.target.blur()"
      >
      <div class="action-items right">
        <v-btn
          v-if="!editing && canEdit"
          icon="mdi-format-color-fill"
          variant="text"
          class="justify-center align-center pt-1 action-icon picker-toggle"
          :class="showPicker ? 'picking action-icon-persist' : ''"
          aria-label="Change Folder Icon"
          @click.stop.prevent="openPickers"
        />
        <v-btn
          :icon="visible ? `mdi-eye-outline` : `mdi-eye-off-outline`"
          variant="text"
          class="action-icon"
          :class="`${!visible ? 'action-icon-persist' : ''} ${showPicker ? 'picking' : ''}`"
          aria-label="Toggle Folder Visibility"
          @click.stop.prevent="toggleVisibility"
        />
        <v-btn
          v-if="!editing && canEdit"
          icon="mdi-delete-outline"
          variant="text"
          class="action-icon"
          :class="`${!visible ? 'action-icon-invisible' : ''} ${showPicker ? 'picking' : ''}`"
          aria-label="Delete Folder"
          @click.stop.prevent="$emit('deleteFolder')"
        />
      </div>
    </div>
    <div
      v-if="showPicker"
      ref="pickerContainer"
      v-click-outside="closePickers"
      class="pickers-container"
      :style="underlayPicker ? 'top: 90%' : 'bottom: 90%'"
    >
      <div class="px-2 pb-1">
        <IconPicker
          v-if="allowFolderMarkerIcons"
          ref="markerIconPicker"
          :icon-picker-data="markerIconPickerData"
        />
        <IconPicker
          ref="customIconPicker"
          :icon-picker-data="customIconPickerData"
        />
        <ColorPicker
          ref="colorPicker"
          class="pt-1"
          :color-picker-data="colorPickerData"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Internal dependencies
import {ColorPickerOptions, IconPickerOptions} from '@/services/GraphicService';
import * as colors from '@/lib/colors';
import {mapState} from 'vuex';
import {markerIconMap, featureIconMap} from '@/services/MapService/Icons';

// Components
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import DragIcon from '@/components/svgs/DragIcon.vue';
import ColorPicker from '@/components/InfoWindow/sections/Toolbar/ColorPicker.vue';
import IconPicker from '@/components/InfoWindow/sections/Toolbar/IconPicker.vue';
import {preventLetterClipping} from '@/lib/ReusableFunctions/Formatters';

const availableIcons = [
  'AccessRoute-Icon',
  'Label-Icon',
  'Folder-Icon',
  'Polygon-Icon',
  'Polyline-Icon',
  'Image-Icon',
  'BlockPlan-Icon',
];


export default {
  name: 'FolderName',
  components: {
    CaretIcon,
    DragIcon,
    ColorPicker,
    IconPicker,
  },
  props: {
    folder: {
      type: Object,
      required: true,
    },
    folderName: {
      type: String,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    dragHandler: {
      type: Function,
      default: () => {},
    },
    dropHandler: {
      type: Function,
      default: () => {},
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
    showBody: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'updateFolder',
    'toggleFolderVisibility',
    'deleteFolder',
    'panToFirstFeature',
  ],
  data() {
    return {
      /** @type {boolean} */
      visible: this.folder.visible,
      /** @type {string} */
      icon: this.folder.icon ?? 'Folder-Icon',
      /** @type {string} */
      color: this.folder.color ?? colors.blueGray,
      /** @type {boolean} */
      editing: false,
      /** @type {string} */
      name: null,
      /** @type {boolean} */
      hovered: false,
      /** @type {NodeJS.Timeout} */
      timeoutID: null,
      /** @type {boolean} */
      dragHover: false,
      /** @type {string} */
      toggleCustom: 'custom',
      /** @type {boolean} */
      showPicker: false,
      /** @type {IconPickerOptions} */
      markerIconPickerData: new IconPickerOptions({
        title: 'Marker Icons',
        getIcon: () => this.icon,
        setIcon: (icon) => {
          // Hacky way to make it so that the sibling pickers remove selections when a marker is clicked
          if (this.$refs.customIconPicker) {
            this.$refs.customIconPicker.clickedIcon = null;
          }
          if (this.$refs.colorPicker) {
            this.$refs.colorPicker.clickedColor = null;
          }
          this.icon = icon;
          this.color = null;
          this.$emit('updateFolder', 'icon', icon);
          this.$emit('updateFolder', 'color', null);
        },
      }),
      /** @type {IconPickerOptions} */
      customIconPickerData: new IconPickerOptions({
        title: 'Feature Icons',
        getIcon: () => this.icon,
        setIcon: (icon) => {
          if (this.$refs.markerIconPicker) {
            this.$refs.markerIconPicker.clickedIcon = null;
          }
          this.icon = icon;
          if (!this.color) {
            this.color = colors.blueGray;
          }
          this.$emit('updateFolder', 'icon', icon);
        },
        icons: [availableIcons],
      }),
      /** @type {ColorPickerOptions} */
      colorPickerData: new ColorPickerOptions({
        getColor: () => markerIconMap.has(this.icon) ? null : this.color,
        setColor: (color) => {
          if (this.$refs.markerIconPicker) {
            this.$refs.markerIconPicker.clickedIcon = null;
          }
          this.color = color;
          // If you change a color and the icon is currently a marker, change it to a folder icon
          if (markerIconMap.has(this.icon)) {
            this.icon = 'Folder-Icon';
          }
          this.$emit('updateFolder', 'color', color);
        },
        allowTransparent: false,
        availableColors: [
          colors.pink,
          colors.red,
          colors.orange,
          colors.yellow,
          colors.green,
          colors.blue,
          colors.purple,
          colors.brown,
          colors.black,
          colors.blueGray,
        ],
      }),
    };
  },
  computed: {
    ...mapState(['allowFolderMarkerIcons']),
    iconClass() {
      const markerIcon = markerIconMap.get(this.icon);
      return markerIcon ? 'placemarker-icon' : 'feature-icon';
    },
    selectedIcon() {
      const markerIconSvg = markerIconMap.get(this.icon)?.svg;
      const featureIconSvg = featureIconMap.get(this.icon)?.svg;
      return markerIconSvg ?? featureIconSvg;
    },
    markerColor() {
      return markerIconMap.get(this.icon)?.hex;
    },
  },
  watch: {
    'folderName': {
      handler(newVal) {
        this.name = newVal;
      },
      immediate: true,
    },
    'folder.visible': {
      handler: function() {
        this.visible = this.folder.visible;
      },
    },
    async editing(newVal) {
      if (!newVal) {
        await this.clipFolderNameText();
      }
    },
  },
  async mounted() {
    await this.clipFolderNameText();
  },
  methods: {
    /**
     * Use timeout function to handle single clicks by emitting the `toggleBody` event and double
     * clicks by showing an input for editing the folder name
     */
    async folderClick() {
      if (!this.timeoutID) {
        // if no timeout is set, this is the first click, possibly a single click
        this.timeoutID = setTimeout(async () => {
          // if the timeout remains set after 200ms, then no other click was received.
          this.timeoutID = null;
          this.$emit('updateFolder', 'showBody', !this.showBody);

          await this.$nextTick();

          if (this.showBody) {
            this.$emit('panToFirstFeature');
          }
        }, 275);
      } else {
        // if a timeout is already set, then this is the second click within 200ms, so it's a double click
        clearTimeout(this.timeoutID);
        this.timeoutID = null;

        // Should not be able to edit the folder name if the map is not editable
        if (!this.canEdit) return;

        this.editing = !this.editing;
        if (this.editing) {
          await this.$nextTick();
          this.$refs.folderName.focus();
        }
      }
    },
    /**
     * Emits the `folderNameChange` event with the current local folder name value
     */
    handleSave() {
      this.editing = false;
      // Only emit the folder name change event if there is a valid name and the name has changed
      if (this.name && this.name !== this.folderName) {
        this.$emit('updateFolder', 'name', this.name);
      } else {
        this.name = this.folderName;
      }
    },
    // Simply sets the dragHover class to null, but called in multiple places so
    // it has it's own wrapper function
    dragEnd() {
      this.dragHover = null;
    },
    /**
     * @param {DragEvent} e - Event object
     * Handles setting the border classes when dragging an object over the folder name
     */
    handleDragClass(e) {
      // Only handle dragover events coming from the feature card
      if (e?.target?.clientHeight >= 42) {
        if (e.offsetY < 0 || e.offsetY > 44) {
          this.dragHover = null;
        } else if (e.offsetY <= 8) {
          this.dragHover = 'drop-top';
        } else if (e.offsetY <= 40) {
          this.dragHover = 'drop-in';
        } else {
          this.dragHover = 'drop-bottom';
        }
      }
    },
    /**
     * @param {DropEvent} e - Event object derived from the drop event
     * Cleans up dragHover and calls the dropHandler callback function
     */
    folderDrop(e) {
      this.dragEnd();
      let offset = 0;
      // The `clientHeight` check is here to prevent calculating erroneous values emitted
      // from children elements
      if (e.offsetY >= 0 && e?.target?.clientHeight >= 42) {
        if (e.offsetY <= 8) {
          offset = -1;
        } else if (e.offsetY > 40 && e.offsetY <= 48) {
          offset = 1;
        }
      }
      this.dropHandler(e, offset);
    },
    openPickers() {
      // If the bucket icon is less than 250 pixels from the top, underlay the pickers
      const top = this.$refs.folderNameContainer.getBoundingClientRect().top;
      this.underlayPicker = top < 250;
      this.showPicker = true;
    },
    closePickers() {
      if (this.showPicker) {
        this.showPicker = false;
      }
    },
    toggleVisibility() {
      this.visible = !this.visible;
      this.$emit('toggleFolderVisibility');
    },
    /**
     * Fits the folder name text to the container width without clipping letters.
     */
    async clipFolderNameText() {
      // Wait for the next tick to ensure the text has been rendered after editing is set to false
      await this.$nextTick();
      const ref = this.$refs.viewFolderName;
      // For each depth, subtract 15px from the textContainerWidth
      const textContainerWidth = 250 - (this.depth * 15);
      preventLetterClipping(ref, textContainerWidth);
    },
  },
};
</script>
<style scoped lang="scss">
.folder-name-container {
  position: relative;
}
.folder-card {
  width: 100%;
  height: 48px;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 3px;
  border: solid 2px transparent;

  input {
    height: 38px;
    width: 100%;
  }

  .action-items {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-right: 2px;
    margin-right: 8px;

    &.right {
      margin-left: auto;
    }
  }

  .action-icon {
    padding: 0px;
    width: 24px;
    height: 24px;
    margin-left: 5px;
    display: none;

    &-persist {
      display: flex;
    }
    &-invisible {
      display: flex;
      visibility: hidden;

      &:hover {
        visibility: visible;
      }
    }

    &:hover {
      // make the hover background larger than the button itself
      :deep(.v-btn__overlay) {
        opacity: 15%;
        top: -4px;
        left: -5px;
        width: 140%;
        height: 140%;
      }
    }
    &.picking {
      visibility: visible;
      display: flex;
      &.picker-toggle {
        // make the hover background larger than the button itself
        :deep(.v-btn__overlay) {
          opacity: 15%;
          top: -4px;
          left: -5px;
          width: 140%;
          height: 140%;
        }
      }
    }
  }

  &.picking {
    background-color: main.$lightGray;
  }
  &:hover {
    background-color: main.$lightGray;
    cursor: pointer;
    .action-icon {
      display: flex;
      visibility: visible;

       &-persist {
        margin-right: 0;
      }
    }
  }
  &.editing-feature {
    .action-icon {
      display: none;
    }
  }
  /* eslint-disable vue-scoped-css/no-unused-selector */
  svg:nth-of-type(3) {
    margin-left: auto;
  }
  svg {
    margin-left: 3px;
  }
  &-name {
    white-space: nowrap;
    overflow: hidden;
  }
}
.drop-top {
  border-top: solid 2px main.$linkBlue;
}
.drop-bottom {
  border-bottom: solid 2px main.$linkBlue;
}
.drop-in {
  border: solid 2px main.$linkBlue;
}

.folder-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;

  .placemarker-icon {
    height: 24px;
    width: 24px;
    margin-right: 10px;
  }
  .feature-icon {
    height: 20px;
    width: 20px;
    margin-right: 14px;
  }
}
.pickers-container {
  position: absolute;
  right: 19%;
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.4));
  z-index: 2;
  background-color: white;
  border-radius: 5px;
}
</style>
